import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { ErrorCard, Field, LoadingBlock, PageHeader, Pill } from "../components/jamora-admin/AdminBits";
import { FilterTabs } from "../components/jamora-admin/ListBits";
import ConfigTab from "../components/jamora-admin/pharmacy/ConfigTab";
import NotificationsTab from "../components/jamora-admin/pharmacy/NotificationsTab";
import PrintingTab from "../components/jamora-admin/pharmacy/PrintingTab";
import UsersTab from "../components/jamora-admin/pharmacy/UsersTab";
import TrainingTab from "../components/jamora-admin/pharmacy/TrainingTab";
import { SalesTab } from "../components/jamora-admin/pharmacy/SalesGroup";
import { PurchasesTab } from "../components/jamora-admin/pharmacy/PurchasesGroup";
import { StockTab } from "../components/jamora-admin/pharmacy/StockGroup";
import { FinanceTab } from "../components/jamora-admin/pharmacy/FinanceGroup";
import { InsuranceTab } from "../components/jamora-admin/pharmacy/InsuranceGroup";
import { MIGRATION_LABELS, migrationDomainsText, canViewBusinessData, canViewPharmacyData, formatCount } from "../lib/jamoraAdmin";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { formatDateTime } from "../lib/tickets";

const yesNo = (value) => (value ? "Oui" : "Non");

const SUPPORT_TABS = [["configuration", "Configuration"], ["comptes", "Comptes"], ["notifications", "Notifications"], ["impression", "Impression"], ["formation", "Formation"]];
const BUSINESS_TABS = [["stock", "Stock"], ["ventes", "Ventes"], ["achats", "Achats"], ["finances", "Finances"], ["tiers-payant", "Tiers payant"]];

// The file of the pharmacy chosen in the context selector (read once by the shell), then its support sections. Read only.
export default function JamoraAdminPharmacy() {
  const { pharmacy } = useOutletContext();
  const { user } = useAuth();
  const { phase, data: p, error, reload } = pharmacy;
  const canSeeAssist = !!user?.features?.assist && hasPermission(user, ["support:handle", "support:view_all"]);

  const [params, setParams] = useSearchParams();
  // Each group of tabs needs its own key: the support sections `view_pharmacy`, the business data `view_business_data`.
  const tabs = [["", "Fiche"], ...(canViewPharmacyData(user) ? SUPPORT_TABS : []), ...(canViewBusinessData(user) ? BUSINESS_TABS : [])];
  const tab = tabs.some(([id]) => id === params.get("tab")) ? params.get("tab") : "";

  if (phase === "loading" || phase === "idle") return <LoadingBlock rows={4} />;
  if (phase === "error") return <ErrorCard message={error} onRetry={reload} />;

  return (
    <div className="space-y-4" data-testid="admin-pharmacy">
      <PageHeader title={p.name} description={p.primary_domain || p.schema} />
      {tabs.length > 1 && <FilterTabs label="Sections de la pharmacie" tabs={tabs} value={tab} onChange={(v) => setParams(v ? { tab: v } : {}, { replace: true })} testid="pharmacy-tab" />}
      {tab === "configuration" && <ConfigTab schema={p.schema} />}
      {tab === "comptes" && <UsersTab schema={p.schema} />}
      {tab === "notifications" && <NotificationsTab schema={p.schema} />}
      {tab === "impression" && <PrintingTab schema={p.schema} />}
      {tab === "stock" && <StockTab schema={p.schema} />}
      {tab === "ventes" && <SalesTab schema={p.schema} />}
      {tab === "achats" && <PurchasesTab schema={p.schema} />}
      {tab === "finances" && <FinanceTab schema={p.schema} />}
      {tab === "tiers-payant" && <InsuranceTab schema={p.schema} />}
      {tab === "formation" && <TrainingTab schema={p.schema} />}
      {tab === "" && <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4" data-testid="pharmacy-identity">
          <h2 className="text-sm font-semibold text-slate-900 mb-1">Identité</h2>
          <dl className="divide-y divide-slate-100">
            <Field label="État"><span className="inline-flex gap-1"><Pill tone={p.active ? "emerald" : "red"}>{p.active ? "Active" : "Désactivée"}</Pill>{p.demo && <Pill tone="violet">Démonstration</Pill>}</span></Field>
            <Field label="Schéma">{p.schema}</Field>
            <Field label="Titulaire">{p.responsable || "—"}</Field>
            <Field label="Type d'officine">{p.type_officine || "—"}</Field>
            <Field label="Région">{p.region || "—"}</Field>
            <Field label="Créée le">{p.created_at ? formatDateTime(p.created_at) : "—"}</Field>
          </dl>
        </Card>
        <Card className="p-4" data-testid="pharmacy-contact">
          <h2 className="text-sm font-semibold text-slate-900 mb-1">Contact et informations légales</h2>
          <dl className="divide-y divide-slate-100">
            <Field label="Adresse">{[p.address, p.address_complement].filter(Boolean).join(" — ") || "—"}</Field>
            <Field label="Téléphone">{p.phone || "—"}</Field>
            <Field label="E-mail">{p.email ? <a className="text-jamora hover:underline" href={`mailto:${p.email}`}>{p.email}</a> : "—"}</Field>
            <Field label="NINEA">{p.ninea || "—"}</Field>
            <Field label="Licence">{p.licence || "—"}</Field>
          </dl>
        </Card>
        <Card className="p-4" data-testid="pharmacy-usage">
          <h2 className="text-sm font-semibold text-slate-900 mb-1">Utilisation</h2>
          <dl className="divide-y divide-slate-100">
            <Field label="Onboarding terminé">{yesNo(p.onboarding_completed)}</Field>
            <Field label="Comptes">{formatCount(p.users_count)}</Field>
            <Field label="Migration Onepharma">{MIGRATION_LABELS[p.migration_status] || p.migration_status}</Field>
            {!!p.migration_domains.length && <Field label="Migration par domaine">{migrationDomainsText(p.migration_domains)}</Field>}
            <Field label="Synchronisation Onepharma">{p.onepharma_sync_active ? `Active${p.onepharma_last_sync_at ? ` · dernière le ${formatDateTime(p.onepharma_last_sync_at)}` : ""}` : "Inactive"}</Field>
            <Field label="Assistance à distance">{p.assist_enabled ? "Activée pour cette pharmacie" : "Désactivée pour cette pharmacie"}{canSeeAssist && <> · <Link className="text-jamora hover:underline" to="/assistance">Ouvrir l'assistance</Link></>}</Field>
          </dl>
        </Card>
        <Card className="p-4" data-testid="pharmacy-domains">
          <h2 className="text-sm font-semibold text-slate-900 mb-1">Adresses</h2>
          <ul className="divide-y divide-slate-100">
            {p.domains.map((d) => (
              <li key={d.domain} className="flex items-center gap-2 py-1.5 text-sm"><span className="min-w-0 break-all text-slate-900">{d.domain}</span>{d.is_primary && <Pill tone="sky">Principale</Pill>}</li>
            ))}
            {!p.domains.length && <li className="py-1.5 text-sm text-slate-500">Aucune adresse enregistrée.</li>}
          </ul>
        </Card>
      </div>}
    </div>
  );
}
